import axios from "axios";

import urls from "@/constants/urls";

/**
 * Signing a donation export's download link.
 *
 * An export signs a link to its file once, when it uploads the file, and that
 * link lives one hour. It is the link stored in the job's `meta.file`, so a
 * download button rendered from `meta.file` goes dead an hour after the export
 * ran while still looking like a working link — clicking it lands on a raw
 * "Access denied" page from the CDN.
 *
 * These helpers ask the backend for a link instead, at the moment the button is
 * clicked, so a download works however long the modal has been open. Nothing
 * here holds a link in state: it is fetched and followed immediately.
 */

/** How the backend reports an export whose file cannot be served. */
export const EXPORT_NOT_FINISHED = "export_not_finished";
export const EXPORT_FILE_UNAVAILABLE = "export_file_unavailable";

/**
 * signProcExportDownload signs a link for an export tracked by its proc session
 * (the synchronous export path, which answers `proc_session_id`).
 *
 * @param {number|string} procId
 * @param {number|string} orgId
 * @returns {Promise<{url: string, expires_at: string, format: string, row_count: number}>}
 */
export function signProcExportDownload(procId, orgId) {
  return axios
    .get(urls.procExportDownload.replace(":oid", orgId).replace(":id", procId))
    .then((res) => res.data.data);
}

/**
 * signTaskExportDownload signs a link for an export tracked by its task row (the
 * deferred export path, which answers `sqs_message_id`).
 *
 * @param {string} referenceId
 * @returns {Promise<{url: string, expires_at: string, format: string, row_count: number}>}
 */
export function signTaskExportDownload(referenceId) {
  return axios
    .get(urls.taskExportDownload.replace(":id", referenceId))
    .then((res) => res.data.data);
}

/**
 * exportDownloadMessage maps a refused signing request to a translation key and
 * its English fallback, so a caller can say what happened instead of showing the
 * raw storage error page this used to end in. Every failure gets a message; an
 * unrecognised one reads as a generic "try again".
 *
 * @param {object} error an axios error
 * @returns {[string, string]} the key and its fallback, for $t(key, fallback)
 */
export function exportDownloadMessage(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.errors?.[0]?.code;

  if (status === 409 || code === EXPORT_NOT_FINISHED) {
    return [
      "dashboard.export_still_running",
      "The export is still running. Please wait for it to finish.",
    ];
  }

  if (status === 410 || code === EXPORT_FILE_UNAVAILABLE) {
    return [
      "dashboard.export_file_unavailable",
      "This export has no file to download any more. Please run the export again.",
    ];
  }

  if (status === 404) {
    return [
      "dashboard.export_not_found",
      "This export could not be found. Please run the export again.",
    ];
  }

  return [
    "dashboard.export_download_failed",
    "The download could not be prepared. Please try again.",
  ];
}

/**
 * followExportDownload navigates to a signed link. The link is short-lived and
 * is never stored, so it is followed as soon as it arrives.
 *
 * @param {{url: string}} link
 */
export function followExportDownload(link) {
  if (!link || !link.url) {
    throw new Error("The export download response carried no url");
  }

  window.location.assign(link.url);
}
